import type { BillingAddressDetails, PaymentProfileOwner } from '@billing/payment-sdk'
import * as Sentry from '@sentry/vue'
import { FetchError } from 'ofetch'
import getCurrentHost from '~/utils/api-utils'

export type PaymentType = keyof typeof PAYMENT_TYPE_ICON

// See AllowedPayment Types https://customer-api.eks-eu-central-1-private.int.billing.ridedev.io/api-docs/webjars/swagger-ui/index.html?configUrl=%2Fapi-docs%2Fv3%2Fopenapi%2Fswagger-config&urls.primaryName=customer#/
// Can be a mix of paymentTypes and creditcardTypes
export const PAYMENT_TYPE_ICON = {
  PAYPAL: 'payment_paypal',
  INVOICE: 'payment_filled',
  WITHDRAWAL: 'payment_filled',
  VISA: 'payment_visa',
  AMEX: 'payment_amex',
  MASTERCARD: 'payment_mastercard',
  IDEAL: 'payment_ideal',
  SOFORT: 'payment_sofort',
  GIROPAY: 'payment_giropay',
  STRIPE_SEPA: 'payment_filled',
  DISCOVER: 'payment_discover',
  DINERS_CLUB: 'payment_dinersclub',
  DIRECTDEBIT: 'payment_debit',
  PARTNER: 'payment_filled',
  EPS: 'payment_eps',
  MOBILE_PAY: 'payment_mobile_pay',
  APPLE_PAY: 'payment_apple_pay',
  GOOGLE_PAY: 'payment_google_pay',
}

export interface PaymentProfileSummary {
  paymentProfileId: number
  paymentProfileUuid: string
  profileName: string
  defaultProfile: boolean
  usable: boolean
  ownProfile?: boolean
  type: 'PRIVATE' | 'BUSINESS' | 'SERVICE'
  eligibleLegalEntities: number[]
  valid: boolean
  payment: {
    type: string
    number?: string
  } | null
}

export interface PaymentProfilesResponse {
  body: {
    paymentProfiles: PaymentProfileSummary[]
  }
}

// https://internal-api.eks-eu-central-1-private.int.billing.ridedev.io/api-docs/swagger-ui/index.html#/Credits/getApplicableCreditBackConfiguration
export interface CreditBackConfiguration {
  percentage: number
  maxAmount: number | null
  currency: string | null
  validFrom: string
  validUntil: string | null
  creditValidityDurationDays: number
  campaignId: string | null
}

export function usePaymentApi() {
  const apiBase: string = '/api/billing/customer'

  const getPaymentProfileSummary = async (token: string) => {
    try {
      return await $fetch<PaymentProfilesResponse>(`${apiBase}/payment-profile-summary`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        baseURL: getCurrentHost(),
      })
    }
    catch (error: unknown) {
      if (error instanceof FetchError) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          Sentry.captureMessage(`Authorization error while fetching payment profiles: ${error.message}`, 'error')
          return null
        }
        else if (error.response?.status === 404) {
          Sentry.captureMessage(`Not Found Error while fetching payment profiles: ${error.message}`, 'error')
        }
      }
      Sentry.captureException(error)
      return null
    }
  }
  /**
   * Updates the billing address details (e.g. fiscal code, VAT number) for a
   * specific profile address. Calls the internal Nuxt server route which reads
   * the HttpOnly session cookie — no token is passed from the client.
   */
  const updateBillingAddressDetails = async (
    profileId: number,
    addressId: number,
    body: BillingAddressDetails,
  ): Promise<BillingAddressDetails | null> => {
    try {
      return await $fetch<BillingAddressDetails>(
        `/api/billing/payment-profiles/${profileId}/address/${addressId}/billing-address-details`,
        {
          method: 'PUT',
          body,
        },
      )
    }
    catch (error: unknown) {
      if (error instanceof FetchError) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          Sentry.captureMessage(`Authorization error while updating billing address details: ${error.message}`, 'error')
          return null
        }
        else if (error.response?.status === 404) {
          Sentry.captureMessage(
            `Profile or address not found while updating billing address details: profileId=${profileId}, addressId=${addressId}`,
            'warning',
          )
          return null
        }
      }
      Sentry.captureException(error)
      return null
    }
  }

  /**
   * Fetches the payment-profile-owner for the currently authenticated customer.
   * Calls the internal Nuxt server route which reads the HttpOnly session cookie —
   * no token is passed from the client.
   */
  const getPaymentProfileOwner = async () => {
    try {
      return await $fetch<PaymentProfileOwner>('/api/billing/payment-profile-owner', {
        method: 'GET',
      })
    }
    catch (error: unknown) {
      if (error instanceof FetchError) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          Sentry.captureMessage(`Authorization error while fetching payment profile owner: ${error.message}`, 'error')
          return null
        }
        else if (error.response?.status === 404) {
          Sentry.captureMessage(`Not found while fetching payment profile owner: ${error.message}`, 'error')
          return null
        }
      }
      Sentry.captureException(error)
      return null
    }
  }

  /** Returns the applicable credit-back configuration, or null if there is none or the call failed. */
  const getCreditBack = async (token: string, locationId: number): Promise<CreditBackConfiguration | null> => {
    try {
      return await $fetch<CreditBackConfiguration | null>('/api/billing/credit-back', {
        method: 'GET',
        query: { location_id: locationId },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }) || null
    }
    catch (error: unknown) {
      Sentry.captureException(error)
      return null
    }
  }

  return {
    getPaymentProfileSummary,
    getPaymentProfileOwner,
    updateBillingAddressDetails,
    getCreditBack,
  }
}
